import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DealDialog from "./DealDialog";
import EditDealDialog from "./EditDealDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PipelineEditor from "./PipelineEditor";
import OrphanDeals from "./OrphanDeals";
import { canManage, canWrite, crmErr, money } from "./helpers";

function LostDialog({ onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="deal-lost-dialog">
        <DialogHeader><DialogTitle>Segna come persa</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Note (facoltative)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. prezzo, tempi, scelto un altro fornitore" data-testid="deal-lost-reason" />
          <p className="mt-1 text-[11px] text-slate-400">Restano nello storico del contatto con data e autore.</p></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={() => onDone(reason.trim())} data-testid="deal-lost-confirm">Segna come persa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DealCard({ d, stages, contacts, write, onMove, onDelete, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: d.id, disabled: !write });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div ref={setNodeRef} style={style} className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${isDragging ? "z-10 opacity-80" : ""}`} data-testid={`deal-${d.id}`}>
      <div {...listeners} {...attributes} className={write ? "cursor-grab" : ""}>
        <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold text-slate-900">{d.title}</p>
          <span className="flex shrink-0 gap-1.5">{write && <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onEdit(d)} className="text-slate-400 hover:text-sky-700" title="Modifica trattativa" aria-label="Modifica trattativa" data-testid={`deal-edit-${d.id}`}><Pencil size={13} /></button>}
          {onDelete && <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onDelete(d)} className="text-slate-400 hover:text-red-600" data-testid={`deal-delete-${d.id}`}><Trash2 size={13} /></button>}</span></div>
        <p className="text-xs text-slate-500">{money(d.value_cents)}{d.contact_id && contacts[d.contact_id] ? ` · ${contacts[d.contact_id]}` : !d.contact_id ? " · senza contatto" : ""}</p>
        {d.lost_reason && <p className="mt-1 text-[11px] text-red-600">Nota: {d.lost_reason}</p>}
      </div>
      {write && <Select value={d.stage_id} onValueChange={(v) => onMove(d, v)}><SelectTrigger className="mt-2 h-7 text-xs" data-testid={`deal-stage-${d.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>}
    </div>
  );
}

function Column({ s, deals, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: s.id });
  const tot = deals.reduce((a, d) => a + d.value_cents, 0);
  return (
    <div ref={setNodeRef} className={`flex w-56 shrink-0 flex-col gap-2 rounded-xl p-2 ${isOver ? "bg-sky-100" : "bg-slate-100"}`} data-testid={`stage-${s.id}`}>
      <div className="px-1"><p className="text-xs font-semibold text-slate-800">{s.name} <span className="font-normal text-slate-500">· {deals.length}</span></p>
        <p className="text-[11px] text-slate-500" data-testid={`stage-total-${s.id}`}>{money(tot)} · probabilità {s.probability}%</p></div>
      {children}
    </div>
  );
}

export default function CrmPipeline({ tid, role, tick, onChanged }) {
  const api = useApi();
  const [p, setP] = useState(null);
  const [contacts, setContacts] = useState({});
  const [lost, setLost] = useState(null);
  const [create, setCreate] = useState(false);
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const load = useCallback(() => Promise.all([api.get(`/t/${tid}/crm/pipeline`), api.get(`/t/${tid}/crm/contacts`)])
    .then(([a, b]) => { setP(a.data); setContacts(Object.fromEntries(b.data.map((c) => [c.id, c.name]))); })
    .catch((e) => { setP({ stages: [], deals: [] }); toast.error(errMsg(e)); }), [api, tid]);
  useEffect(() => { load(); }, [load, tick]);
  const move = async (d, stage, reason = null) => {
    if (stage === d.stage_id) return;
    if (p.stages.find((s) => s.id === stage)?.kind === "lost" && reason === null) { setLost({ d, stage }); return; }
    try { await api.post(`/t/${tid}/crm/deals/${d.id}/move`, { stage_id: stage, note: reason || "" }); setLost(null); load(); onChanged?.(); }
    catch (e) { toast.error(crmErr(e, errMsg)); }
  };
  const remove = async () => { const d = del; setDel(null); try { await api.delete(`/t/${tid}/crm/deals/${d.id}`); toast.success("Opportunità nel cestino (ripristinabile per 30 giorni)"); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); } };
  if (!p) return <p className="text-sm text-slate-500">Caricamento…</p>;
  const write = canWrite(role);
  const onDragEnd = ({ active, over }) => { const d = over && p.deals.find((x) => x.id === active.id); if (d) move(d, over.id); };
  return (
    <div className="space-y-3" data-testid="crm-pipeline">
      <OrphanDeals tid={tid} canWrite={write} tick={tick} onChanged={() => { load(); onChanged?.(); }} />
      <div className="flex flex-wrap items-center gap-2"><p className="text-xs text-slate-500">Trascina le schede tra le fasi o scegli la fase dal menu. «Perso» chiede il motivo.</p>
        {canManage(role) && <Button size="sm" variant="outline" className="ml-auto" onClick={() => setEdit(true)} data-testid="pipeline-edit"><Settings2 size={14} className="mr-1" />Modifica fasi</Button>}
        {write && <Button size="sm" className={canManage(role) ? "" : "ml-auto"} onClick={() => setCreate(true)} data-testid="deal-new"><Plus size={14} className="mr-1" />Nuova opportunità</Button>}</div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="pipeline-board">{p.stages.map((s) => {
          const ds = p.deals.filter((d) => d.stage_id === s.id);
          return <Column key={s.id} s={s} deals={ds}>{ds.map((d) => <DealCard key={d.id} d={d} stages={p.stages} contacts={contacts} write={write} onMove={move} onDelete={canManage(role) && !d.source ? setDel : null} onEdit={setEditDeal} />)}</Column>;
        })}</div>
      </DndContext>
      {lost && <LostDialog onClose={() => setLost(null)} onDone={(r) => move(lost.d, lost.stage, r)} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Eliminare «${del?.title}»?`} destructive confirmLabel="Elimina"
        description="L'opportunità va nel cestino e resta ripristinabile per 30 giorni." onConfirm={remove} />
      {edit && <PipelineEditor tid={tid} pipeline={p} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); onChanged?.(); }} />}
      {create && <DealDialog tid={tid} stages={p?.stages || []} onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); onChanged?.(); }} />}
      {editDeal && <EditDealDialog tid={tid} deal={editDeal} stages={p.stages} contact={editDeal.contact_id ? { id: editDeal.contact_id, label: contacts[editDeal.contact_id] || "Contatto" } : null}
        onClose={() => setEditDeal(null)} onSaved={() => { setEditDeal(null); load(); onChanged?.(); }} />}
    </div>
  );
}
